import React, { useEffect, useRef } from "react";
import { motion } from "framer-motion";
import { ArrowRight, Sparkles, Smartphone, Search, Zap, BadgeIndianRupee } from "lucide-react";
import { MouseGlow } from "./effects/MouseGlow";
import { Particles } from "./effects/Particles";
import { Counter } from "./effects/Counter";

const ease = [0.16, 1, 0.3, 1];

const trust = [
  { icon: Smartphone, label: "Mobile Optimized" },
  { icon: Search, label: "SEO Ready" },
  { icon: Zap, label: "Fast Delivery" },
  { icon: BadgeIndianRupee, label: "Affordable Pricing" },
];

export const Hero = ({ onPrimary, onSecondary }) => {
  const bgRef = useRef(null);

  useEffect(() => {
    const isTouch = window.matchMedia("(pointer: coarse)").matches;
    if (isTouch) return;
    const onScroll = () => {
      if (!bgRef.current) return;
      const y = Math.min(window.scrollY, 600) * 0.2;
      bgRef.current.style.transform = `translate3d(0, ${y}px, 0)`;
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <section
      id="top"
      data-testid="hero-section"
      className="relative pt-32 pb-24 sm:pt-40 sm:pb-32 overflow-hidden"
    >
      {/* Background */}
      <div ref={bgRef} className="absolute inset-0 will-change-transform">
        <div className="absolute inset-0 grid-pattern opacity-60 pointer-events-none" />
        <div
          className="absolute inset-0 pointer-events-none opacity-[0.12] mix-blend-screen"
          style={{
            backgroundImage:
              "url('https://images.pexels.com/photos/21031387/pexels-photo-21031387.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940')",
            backgroundSize: "cover",
            backgroundPosition: "center",
          }}
        />
      </div>
      <div className="absolute -top-40 -right-40 w-[600px] h-[600px] bg-[#D4AF37]/10 blur-[140px] rounded-full pointer-events-none" />
      <div className="absolute top-1/2 -left-40 w-[500px] h-[500px] bg-[#A88824]/10 blur-[160px] rounded-full pointer-events-none" />

      <MouseGlow />
      <Particles count={22} />

      <div className="relative max-w-7xl mx-auto px-6 sm:px-8">
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, ease }}
          className="inline-flex items-center gap-2 glass-gold rounded-full px-4 py-1.5 mb-8"
        >
          <Sparkles className="w-3.5 h-3.5 text-[#D4AF37]" />
          <span className="text-[11px] tracking-[0.2em] uppercase text-[#D4AF37] font-medium">
            Premium Web Development · India
          </span>
        </motion.div>

        <div className="grid lg:grid-cols-12 gap-12 items-end">
          <div className="lg:col-span-8">
            <motion.h1
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.9, ease, delay: 0.05 }}
              className="font-display text-4xl sm:text-6xl lg:text-7xl xl:text-[88px] leading-[1.02] tracking-tighter font-light text-white"
            >
              Your competitors are <br className="hidden sm:block" />
              winning customers <span className="gold-text italic font-normal">online</span> while
              you're <span className="text-[#B0B0B0]">missing opportunities.</span>
            </motion.h1>

            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.9, ease, delay: 0.15 }}
              className="mt-8 max-w-2xl text-base sm:text-lg text-[#B0B0B0] leading-relaxed"
            >
              Every day without a professional website means potential customers choose
              businesses they can easily find and trust online. I build conversion-focused
              websites that turn visitors into paying customers.
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.9, ease, delay: 0.25 }}
              className="mt-10 flex flex-col sm:flex-row gap-4"
            >
              <button
                data-testid="hero-primary-cta"
                onClick={onPrimary}
                className="btn-gold-glow group inline-flex items-center justify-center gap-2 bg-[#D4AF37] hover:bg-[#E6C35C] text-black font-semibold rounded-full px-7 py-4 transition-all shadow-[0_0_30px_rgba(212,175,55,0.3)] hover:shadow-[0_0_50px_rgba(230,195,92,0.55)]"
              >
                Get My Website
                <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
              </button>
              <button
                data-testid="hero-secondary-cta"
                onClick={onSecondary}
                className="inline-flex items-center justify-center gap-2 bg-transparent text-white border border-[#D4AF37]/40 hover:border-[#D4AF37] hover:bg-[#D4AF37]/10 rounded-full px-7 py-4 transition-all"
              >
                Book Free Consultation
              </button>
            </motion.div>

            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 1.2, delay: 0.5 }}
              className="mt-12 grid grid-cols-2 sm:grid-cols-4 gap-4 max-w-2xl"
            >
              {trust.map((t) => (
                <div
                  key={t.label}
                  data-testid={`trust-${t.label.toLowerCase().replace(/\s/g, "-")}`}
                  className="flex items-center gap-2 text-sm text-[#B0B0B0]"
                >
                  <span className="w-7 h-7 rounded-full glass-gold flex items-center justify-center">
                    <t.icon className="w-3.5 h-3.5 text-[#D4AF37]" />
                  </span>
                  <span>{t.label}</span>
                </div>
              ))}
            </motion.div>
          </div>

          <motion.div
            initial={{ opacity: 0, x: 30 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 1, ease, delay: 0.4 }}
            className="lg:col-span-4 hidden lg:block"
          >
            <div className="relative">
              <div className="glass rounded-3xl p-6">
                <div className="text-xs tracking-[0.2em] uppercase text-[#D4AF37] mb-3">
                  Live counter
                </div>
                <div className="font-display text-5xl text-white">
                  <Counter to={27} prefix="+" />
                </div>
                <p className="text-sm text-[#B0B0B0] mt-2">
                  Businesses scaled this year
                </p>
                <div className="mt-6 h-px bg-gradient-to-r from-transparent via-[#D4AF37]/40 to-transparent" />
                <div className="mt-6 grid grid-cols-2 gap-4">
                  <div>
                    <div className="font-display text-2xl text-white">4.9★</div>
                    <p className="text-xs text-[#737373] mt-1">Avg. rating</p>
                  </div>
                  <div>
                    <div className="font-display text-2xl text-white">7d</div>
                    <p className="text-xs text-[#737373] mt-1">Avg. delivery</p>
                  </div>
                </div>
              </div>
              <div className="absolute -bottom-6 -right-6 glass-gold rounded-2xl px-5 py-4">
                <div className="text-[10px] uppercase tracking-[0.2em] text-[#D4AF37]">
                  Slots
                </div>
                <div className="text-sm text-white mt-1 font-medium">
                  Only 2 left this month
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};
